import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateContactsAndWorkforces } from "src/utils";
import { IWorkforce } from "../../interfaces/IWorkforce";
import { getWorkforceRate } from "../actions/getWorkforceRate";
import { updateWorkforce } from "../actions/updateWorforce";

export const useUpdateWorkforce = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, data, params }: { uid: string; data: Partial<IWorkforce>; params?: Record<string, any> }) =>
      updateWorkforce(uid, data, params),

    onSuccess: async (updatedWorkforce, variables) => {
      const targetUid = updatedWorkforce?.uid || updatedWorkforce?.data?.uid || variables.uid;
      queryClient.invalidateQueries({ queryKey: ["workforces"] });
      queryClient.invalidateQueries({ queryKey: ["total-workforce"] });
      if (targetUid) {
        queryClient.invalidateQueries({ queryKey: ["workforce", targetUid] });
        if (updatedWorkforce) {
          queryClient.setQueryData(["workforce", targetUid], updatedWorkforce);
        }
        queryClient.invalidateQueries({ queryKey: ["workforceRate", targetUid] });
      }
      if (targetUid && updatedWorkforce) {
        queryClient.setQueryData<any[]>(["workforces"], (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((w: any) => (w.uid === targetUid ? { ...w, ...updatedWorkforce } : w));
        });
      }
      await invalidateContactsAndWorkforces(queryClient);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || "Error al actualizar";
      console.error(message);
    },
  });
};

export const useEditWorkforceCoverageAreas = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, coverageAreas, params }: { uid: string; coverageAreas: any[]; params?: Record<string, any> }) =>
      updateWorkforce(uid, { coverageAreas } as any, params),

    onSuccess: async (updatedWorkforce, variables) => {
      const targetUid = updatedWorkforce?.uid || updatedWorkforce?.data?.uid || variables.uid;
      queryClient.invalidateQueries({ queryKey: ["workforces"] });
      if (targetUid) {
        queryClient.invalidateQueries({ queryKey: ["workforce", targetUid] });
        queryClient.setQueryData(["workforce", targetUid], (old: any) => ({
          ...(old || {}),
          ...(updatedWorkforce || {}),
          coverageAreas: updatedWorkforce?.coverageAreas || variables.coverageAreas,
        }));
      }
      if (targetUid) {
        queryClient.setQueryData<any[]>(["workforces"], (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((w: any) =>
            w.uid === targetUid
              ? { ...w, ...(updatedWorkforce || {}), coverageAreas: updatedWorkforce?.coverageAreas || variables.coverageAreas }
              : w,
          );
        });
      }
      await invalidateContactsAndWorkforces(queryClient);
    },
    onError: (error: any) => {
      console.error(error?.response?.data?.message || "Error updating coverage areas");
    },
  });
};

export const useGetRateWorkforce = (uid: string) => {
  return useQuery({
    queryKey: ["workforceRate", uid],
    queryFn: () => getWorkforceRate(uid),
    enabled: !!uid,
    staleTime: 1000 * 60,
  });
};
